import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, HStack, Badge, IconButton, Input,
  VStack, Flex, useToast, InputGroup, InputLeftElement,
} from '@chakra-ui/react';
import { FiX, FiSearch, FiUser } from 'react-icons/fi';
import api from '../api';

/**
 * AddContactModal
 *
 * entityType: 'project' | 'task' | 'subtask'
 * entityId:   the id
 * entityName: display name (shown in header)
 *
 * All three use junction tables:
 *   project  → project_contacts
 *   task     → task_contacts
 *   subtask  → sub_task_contacts
 */
export default function AddContactModal({ entityType, entityId, entityName, isOpen, onClose, onSaved }) {
  const [assigned, setAssigned]   = useState([]);  // [{contact_id, first_name, last_name, company_name}]
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [saving, setSaving]       = useState(false);
  const debounce                  = useRef(null);
  const toast                     = useToast();

  const apiBase = entityType === 'project'
    ? `/projects/${entityId}/contacts`
    : entityType === 'task'
      ? `/tasks/${entityId}/contacts`
      : `/sub-tasks/${entityId}/contacts`;

  const fetchAssigned = async () => {
    if (!entityId) return;
    try {
      const { data } = await api.get(apiBase);
      setAssigned(data);
    } catch(e) { setAssigned([]); }
  };

  useEffect(() => {
    if (isOpen && entityId) {
      fetchAssigned();
      setQuery('');
      setResults([]);
    }
  }, [isOpen, entityId]); // eslint-disable-line

  // Live search
  useEffect(() => {
    clearTimeout(debounce.current);
    if (!query.trim()) { setResults([]); return; }
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(query.trim())}&limit=10`);
        setResults(data);
      } catch(e) { setResults([]); }
    }, 250);
    return () => clearTimeout(debounce.current);
  }, [query]);

  const handleAdd = async (contact) => {
    // Already assigned?
    const alreadyId = contact.contact_id || contact.id;
    if (assigned.find(a => (a.contact_id || a.id) === alreadyId)) {
      toast({ title: 'Already assigned', status: 'info', duration: 1500 });
      return;
    }
    setSaving(true);
    try {
      await api.post(apiBase, { contact_id: alreadyId });
      await fetchAssigned();
      setQuery('');
      setResults([]);
      toast({ title: 'Contact added', status: 'success', duration: 1500 });
      if (onSaved) onSaved();
    } catch(e) {
      toast({ title: 'Failed to add', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const handleRemove = async (contact) => {
    const cid = contact.contact_id || contact.id;
    setSaving(true);
    try {
      await api.delete(`${apiBase}/${cid}`);
      setAssigned(p => p.filter(a => (a.contact_id || a.id) !== cid));
      if (onSaved) onSaved();
    } catch(e) {
      toast({ title: 'Failed to remove', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  const typeLabel = entityType === 'project' ? 'Project' : entityType === 'task' ? 'Task' : 'Sub-task';
  const typeColor = entityType === 'project' ? 'teal' : entityType === 'task' ? 'blue' : 'purple';

  const contactName = (c) => `${c.first_name || ''} ${c.last_name || ''}`.trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          <HStack spacing={2}>
            <FiUser size={15} />
            <Text>Add Contact</Text>
          </HStack>
          <HStack spacing={1} mt={1}>
            <Badge colorScheme={typeColor} fontSize="8px">{typeLabel}</Badge>
            <Text fontSize="11px" fontWeight="400" color="gray.600" noOfLines={1}>{entityName}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={3} align="stretch">

            {/* Currently assigned contacts */}
            <Box>
              <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>
                Assigned Contacts
              </Text>
              {assigned.length === 0 ? (
                <Text fontSize="11px" color="gray.400">No contacts assigned yet.</Text>
              ) : (
                <VStack spacing={1} align="stretch">
                  {assigned.map(c => {
                    const cid = c.contact_id || c.id;
                    return (
                      <Flex key={cid} align="center" justify="space-between"
                        bg="gray.50" px={2} py={1} borderRadius="md" border="1px" borderColor="gray.100">
                        <Box>
                          <Text fontSize="12px" fontWeight="600">{contactName(c)}</Text>
                          {c.company_name && (
                            <Text fontSize="10px" color="gray.500">{c.company_name}</Text>
                          )}
                        </Box>
                        <IconButton icon={<FiX size={12} />} size="xs" variant="ghost" colorScheme="red"
                          onClick={() => handleRemove(c)} isLoading={saving}
                          aria-label="Remove contact" />
                      </Flex>
                    );
                  })}
                </VStack>
              )}
            </Box>

            {/* Search & add */}
            <Box>
              <Text fontSize="9px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>
                Search & Add Contact
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <FiSearch color="gray" size={13} />
                </InputLeftElement>
                <Input
                  pl={8} placeholder="Search by name, email or company…"
                  value={query} onChange={e => setQuery(e.target.value)}
                  autoComplete="off"
                />
              </InputGroup>

              {results.length > 0 && (
                <Box mt={1} border="1px" borderColor="gray.200" borderRadius="md"
                  maxH="160px" overflowY="auto" bg="white" boxShadow="sm">
                  {results.map(r => {
                    const rid = r.contact_id || r.id;
                    const alreadyAssigned = !!assigned.find(a => (a.contact_id || a.id) === rid);
                    return (
                      <Flex key={rid} align="center" justify="space-between"
                        px={3} py={1.5} _hover={{ bg: 'blue.50' }} cursor="pointer"
                        opacity={alreadyAssigned ? 0.5 : 1}
                        onClick={() => !alreadyAssigned && handleAdd(r)}>
                        <Box>
                          <Text fontSize="12px" fontWeight="500">
                            {r.first_name} {r.last_name || ''}
                          </Text>
                          {r.company_name && (
                            <Text fontSize="10px" color="gray.500">{r.company_name}</Text>
                          )}
                        </Box>
                        {alreadyAssigned
                          ? <Badge colorScheme="gray" fontSize="8px">Added</Badge>
                          : <Badge colorScheme={typeColor} fontSize="8px" variant="outline">+ Add</Badge>
                        }
                      </Flex>
                    );
                  })}
                </Box>
              )}

              {query.trim() && results.length === 0 && (
                <Text fontSize="11px" color="gray.400" mt={1}>No contacts found.</Text>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button size="sm" onClick={onClose}>Done</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
